import { Fuson360SetupDataExport } from './Fusion360SetupData.interfaces';
import { MachineObject } from './Machines.interfaces';
import { ManufacturingRoute } from './Manufacturing.Interface';
import { MaterialObject, IVendorMaterial } from './Material.Interface';
import { Material, MaterialReservation } from './MaterialInventory.interface';
import { StatusOrderLineItem } from './OrderStatus.Interface';
import { VSCPartData } from './VersaBuilt.Interface';
import type {
	CleaningRequirement,
	ProcessRequirements,
	ProcessSpecRef,
} from './ProcessCatalog.Interface';


export type OrderType = 'external' | 'internal';
export type InternalCategory = 'tooling' | 'fixture' | 'rd' | 'rework' | 'maintenance' | 'training' | 'other';
export type InternalPriority = 'low' | 'medium' | 'high' | 'urgent';
export type Department = 'production' | 'engineering' | 'maintenance' | 'warehouse' | 'quality' | 'other';
export type InternalFileAnalysisStatus = 'none' | 'pending' | 'in_progress' | 'completed' | 'failed';

export interface OrderProps {
	id: string;
	line_items: Lineitem[];
	modified_time: string;
	partsbadger_api_id: number;
	name: string;
	filter?: any;
	created_time: string;
	status: string;
	status_fastlane: string;
	additional_requirements: string[] | null;
	internal_notes: string;
	in_house_requirements: string;
	ready_for_production: boolean;
	revenue?: string | null;
	material_cost?: string | null;
	material_cost_percent?: string | null;
	order_type: OrderType;
	internal_category?: InternalCategory | null;
	requested_by?: number | null;
	department?: Department | null;
	// Internal orders start life as drafts (hidden from every production
	// surface) and flip to false once `orders/<id>/finalize/` succeeds.
	is_draft?: boolean;
	requires_inspection_sample_labeling?: boolean;
	// The order's bulk choice. New line items inherit it; applying it from the
	// order page rewrites every existing line item.
	handling_spec?: ProcessSpecRef | null;
	cleaning_spec?: ProcessSpecRef | null;
}

export interface OrderListProps {
	id: string;
	name: string;
	status: string;
	status_fastlane: string;
	line_items_qty: number;
	total_qty_in_line_item: number;
	soonest_due_date: string;
	soonest_shop_due_date?: {
		date: string;
		original?: string | null;
		revised?: string | null;
	} | null;
	methods: Array<{
		name: string;
		qty: number;
	}>;
	buildings: Array<{ id: string; name: string }>;
	created_time: string;
	revenue?: string | number | null;
	order_type: OrderType;
	internal_category?: InternalCategory | null;
	department?: Department | null;
	internal_priority?: InternalPriority | null;
	// Internal orders start life as drafts (hidden from every production
	// surface) until `orders/<id>/finalize/` succeeds; OrdersTable shows a
	// gray "Draft" tag beside the name while this is true.
	is_draft?: boolean;
}

export enum ProductionMethod {
	Swiss = 'Swiss',
}

export interface Lineitem {
	ballooning_project_id: string;
	lot_inspection_project_id?: string | null;
	id: string;
	machine: MachineObject;
	material?: MaterialObject;
	material_request?: any;
	unit: string;
	created_time: string;
	statuses: StatusOrderLineItem[];
	modified_time: string;
	partsbadger_api_id: number;
	name: string;
	preview_vendor_material?: IVendorMaterial;
	preview_material?: Material;
	preview_vendor_material_length?: number;
	preview_material_length?: number;
	material_length?: string;
	quantity: number;
	delivered_quantity: number;
	remaining_quantity: number;
	cancelled_quantity: number;
	updated_quantity: number;
	material_name: string;
	order: {
		id: string;
		name: string;
		order_type?: 'internal' | 'external';
		is_draft?: boolean;
	} | null;
	material_order?: any;
	manufacturing_route: ManufacturingRoute;
	material_reservations: MaterialReservation[];
	material_reservations_count: number;
	total_reserved_material_length: number;
	reserved_material_lengths_count: number;
	total_reserved_material_cut_quantity: number;
	// PTB-5965: scrap pieces already allocated across this line item's
	// reservations. Used to pre-fill later reservations with only the remaining
	// order-level scrap allowance so the total never exceeds the table value.
	total_reserved_scrap_quantity?: number;
	building?: { id: string; name: string } | null;
	locked_building?: { id: string; name: string } | null;
	width: string;
	height: string;
	length: string;
	bbox_volume: string;
	volume: string;
	partsbadger_production_method: string;
	partsbadger_production_quoting_notes: string;
	required_part_ship_date: string;
	required_partner_ship_date: string;
	revised_partner_ship_date?: string | null;
	shop_due_date?: string | null;
	// Only present on responses that went through the queue or kitting filters.
	scheduling_start_date?: string | null;
	// Only present on the kitting board, after PTB-6764 classification.
	queue_class?: string | null;
	vendor_requirements: string[];
	lead_time: string | number;
	finish_name: string;
	coating_name: string;
	// Finishing is derived from `finish_name`; handling and cleaning are picked
	// per line item (inheriting the order's bulk choice, then the catalog
	// default). `process_requirements` is what the tags and the tablet read.
	// Detail payloads only.
	handling_spec?: ProcessSpecRef | null;
	cleaning_spec?: ProcessSpecRef | null;
	finishing_spec?: ProcessSpecRef | null;
	process_requirements?: ProcessRequirements;
	// List payloads only: the special cleaning the delivery gate asks about.
	cleaning_requirement?: CleaningRequirement | null;
	step_file: string;
	drawing_file: string;
	thumbnail_file: string;
	file_3djs: string;
	product_zid: string;
	ballooned_drawing_file?: string;
	is_keyence_program_required: boolean;
	is_additional_tooling_required: boolean;
	requires_inspection_sample_labeling?: boolean;
	status: string;
	delivery_status?: 'Not Started' | 'Partial Delivery' | 'Delivered in Full';
	ballooning_status: string;
	blank_fai_form_creation_status: string;
	keyence_programming_status: string;
	material_selection_status: string;
	material_ordering_status: string;
	material_prep_status: string;
	tooling_selection_status: string;
	tooling_ordering_status: string;
	cam_programming_status: string;
	cart_assembly_status: string;
	machine_setup_status: string;
	machining_status: string;
	shipping_status: string;
	is_completed?: boolean;
	fusion_360_item_id?: string | null;
	fusion_360_design_export_id?: string | null
	fusion_360_design_urn?: string | null
	fusion_360_design_name?: string | null
	part_transform_matrix: number[];
	// Async OCC geometry (PTB-6193): populated once the SFA's
	// fastlane-geometry service finishes a rotate-part / align-by-face /
	// align-face-to-x-normal request published by this OLI. The frontend
	// polls `GET orders/line-items/{id}/` and watches `geometry_status`
	// flip from "pending" to "ready" (or "failed").
	geometry_status?: 'pending' | 'ready' | 'failed' | null;
	geometry_correlation_id?: string | null;
	part_bbox_global_x?: number | null;
	part_bbox_global_y?: number | null;
	part_bbox_global_z?: number | null;
	stock_facing_amount: string;
	fusion_360_cam_data_exports?: Fuson360SetupDataExport[];
	vsc_part_data?: VSCPartData[];
	quality_status: string;
	aql_table: string | null;
	run_difficulty: number | null;
	machine_time_minutes: number | null;
	machine_setup_time_minutes: number | null;
	material_cutting_time_minutes: number | null;
	cam_data_exists?: boolean;
	has_a_material_certificate?: boolean;
	scheduling_job_id?: string | null;
	swiss_program_name?: string;
	due_date?: string;
	revenue?: string | number | null;
	revenue_changed_by?: number | null;
	revenue_changed_at?: string | null;
	material_cost?: string | number | null;
	material_cost_percent?: string | number | null;
	revenue_per_part?: string | number | null;
	is_internal: boolean;
	internal_step_file?: string | null;
	internal_drawing_file?: string | null;
	internal_thumbnail_file?: string | null;
	// SFA full-analysis lifecycle for the uploaded internal 3D file. Mirrors the
	// backend `OrderLineItem.INTERNAL_ANALYSIS_*` constants; the internal-order
	// wizard polls this until every line item reaches "completed".
	internal_file_analysis_status?: InternalFileAnalysisStatus;
}

export interface GetQueryId {
	id: string;
}
